import { PartLinkTypes } from '~/app/data/statuses/issue';
import { formatPrice } from '~/app/helpers/format';
import {
  EntityStoreKey,
  getEntityStoreKey,
  isEntityEditable,
} from '~/app/common/lineItems/helper';
import { Material, SaveMaterialItem, SaveMaterialPayload } from '~/types';

export type MaterialStoreKey = EntityStoreKey;

export const VARIOUS_MATERIAL_ID = 0;

export const getMaterialStoreKey = (type: PartLinkTypes): MaterialStoreKey =>
  getEntityStoreKey(type);

export const isMaterialsEditable = (
  type: PartLinkTypes,
  status: string | number | undefined
): boolean => isEntityEditable(type, status);

export const isVariousMaterial = (material: Material): boolean =>
  Number(material.id) === VARIOUS_MATERIAL_ID;

const materialKey = (material: Material) => String(material.id ?? '');

const normalizeMaterial = (material: Material) => ({
  id: materialKey(material),
  name: material.name ?? '',
  comment: material.comment ?? '',
  pricePerUnit: Number(material.pricePerUnit) || 0,
  quantity: Number(material.quantity) || 0,
  type: material.type,
  costId: material.costId ?? null,
  customerDiscount: material.customerDiscount ?? 0,
});

/** Compare draft vs last-saved list, ignoring `added` and excluding `deleted` draft rows. */
export const isTempMaterialsDirty = (
  tempMaterials: Material[],
  materials: Material[]
): boolean => {
  const current = tempMaterials
    .filter(m => !m.deleted)
    .map(normalizeMaterial)
    .sort((a, b) => a.id.localeCompare(b.id));

  const saved = materials
    .map(normalizeMaterial)
    .sort((a, b) => a.id.localeCompare(b.id));

  if (current.length !== saved.length) {
    return true;
  }

  return current.some((item, index) => {
    const other = saved[index];
    return (
      item.id !== other.id ||
      item.name !== other.name ||
      item.comment !== other.comment ||
      item.pricePerUnit !== other.pricePerUnit ||
      item.quantity !== other.quantity ||
      item.type !== other.type ||
      item.costId !== other.costId ||
      item.customerDiscount !== other.customerDiscount
    );
  });
};

export const toSaveMaterialItem = (material: Material): SaveMaterialItem => {
  const isVarious = isVariousMaterial(material);

  return {
    id: material.id,
    costId: material.costId ?? null,
    name: isVarious ? null : (material.name ?? null),
    pricePerUnit: Number(material.pricePerUnit) || 0,
    quantity: Number(material.quantity) || 0,
    comment: material.comment ?? null,
    ...(material.customerDiscount !== undefined ? { customerDiscount: material.customerDiscount } : {}),
    ...(material.added ? { added: true } : {}),
    ...(material.deleted ? { deleted: true } : {}),
  };
};

export const toSaveMaterialPayload = (
  parentId: string | number,
  materials: Material[]
): SaveMaterialPayload => ({
  id: String(parentId),
  materials: materials.map(toSaveMaterialItem),
});

export const formatMaterialPrice = formatPrice;
